import { formatDistanceToNow } from 'date-fns';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import {
  DemandExampleTemplate,
  useDeleteTemplateMutation,
  useDuplicateTemplateMutation,
} from '@/services/api/demandExampleTemplateService';

interface TemplatesListProps {
  demandExampleId: string;
  templates: DemandExampleTemplate[] | undefined;
  onEdit: (tpl: DemandExampleTemplate) => void;
}

export const TemplatesList = ({
  demandExampleId,
  templates,
  onEdit,
}: TemplatesListProps) => {
  const [deleteTemplate, { isLoading: isDeleting }] =
    useDeleteTemplateMutation();
  const [duplicateTemplate] = useDuplicateTemplateMutation();
  const navigate = useNavigate();

  const handleDelete = async (tpl: DemandExampleTemplate) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await deleteTemplate({ demandExampleId, templateId: tpl.id }).unwrap();
      toast.success('Template deleted');
    } catch {
      toast.error('Failed to delete template');
    }
  };

  const handleDuplicate = async (tpl: DemandExampleTemplate) => {
    try {
      const newTpl = await duplicateTemplate({
        demandExampleId,
        templateId: tpl.id,
      }).unwrap();
      toast.success('Template copied');
      navigate(
        `/demand-examples/${demandExampleId}/templates/${newTpl.id}/edit`,
      );
    } catch {
      toast.error('Failed to copy template');
    }
  };

  if (!templates?.length) {
    return <p className="text-sm text-muted-foreground">No templates yet.</p>;
  }

  return (
    <div className="overflow-auto">
      <table className="min-w-full text-sm border">
        <thead className="bg-muted">
          <tr>
            <th className="px-3 py-2 text-left font-semibold">Name</th>
            <th className="px-3 py-2 text-left font-semibold">Sections</th>
            <th className="px-3 py-2 text-left font-semibold">Last Modified</th>
            <th className="px-3 py-2" aria-label="actions" />
          </tr>
        </thead>
        <tbody>
          {templates.map((tpl) => (
            <tr
              key={tpl.id}
              className="hover:bg-muted/50 cursor-pointer"
              onClick={() =>
                navigate(
                  `/demand-examples/${demandExampleId}/templates/${tpl.id}`,
                )
              }
            >
              <td className="px-3 py-2 whitespace-nowrap">{tpl.name}</td>
              <td className="px-3 py-2 whitespace-nowrap">
                {tpl.sections_count ?? '—'}
              </td>
              <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                {formatDistanceToNow(new Date(tpl.modified_at), {
                  addSuffix: true,
                })}
              </td>
              <td
                className="px-3 py-2 whitespace-nowrap"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                <Button plain onClick={() => onEdit(tpl)} aria-label="Edit">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  plain
                  disabled={isDeleting}
                  onClick={() => handleDelete(tpl)}
                  aria-label="Delete"
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
                <Button
                  plain
                  onClick={() => handleDuplicate(tpl)}
                  aria-label="Duplicate"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
